import { Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FcViewDetails } from 'react-icons/fc';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from 'react-table';
import { Col, Row } from 'reactstrap';
import ButtonComponent from '../../components/ButtonComponent';
import InputComponent from '../../components/InputComponent';
import Layout from '../../components/Layout/Layout';
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import Routes from '../../helpers/Routes';
import Toast from '../../helpers/Toast';
import { Utilities } from '../../helpers/Utilities';
import { ICountryDTO } from '../../models/Country/ICountryDTO';
import { Parameter } from '../../models/Parameter';
import { CountryService } from '../../services/CountryService';

export default function CountryIndex() {
    const intl = useIntl()
    const pageSizes: number[] = [5, 10, 15, 30];
    const [data, setData] = useState<ICountryDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const navigate = useNavigate();
    const service = new CountryService();

    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<SortingRule<object>[] | null>(null);
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);

    const [initialSortBy] = useState<SortingRule<object>[]>(Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder"));
    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(Utilities.LoadPageSizeFromURLQuery(pageSizes, 10));

    const [goToPage, setGoToPage] = useState<number>();

    const [name, setName] = useState<string>(Utilities.LoadParameterFromURLQuery("name", "string", ""));
    const [iso2Code, setIso2Code] = useState<string>(Utilities.LoadParameterFromURLQuery("iso2Code", "string", ""));
    const [iso3Code, setIso3Code] = useState<string>(Utilities.LoadParameterFromURLQuery("iso3Code", "string", ""));
    const [searchParams] = useSearchParams();

    const columns = React.useMemo(
        () => [

            {
                Header: "",
                accessor: " ",
                Cell: (props: any) => OperationsCell(props.cell.row.original.id, "country"),
            },
            {
                Header: <FormattedMessage id="Screens.Country.Table.Column1" />,
                accessor: "name",
                width: 50,
            },
            {
                Header: <FormattedMessage id="Screens.Country.Table.Column2" />,
                accessor: "iso2Code",
                width: 25,
            },
            {
                Header: <FormattedMessage id="Screens.Country.Table.Column3" />,
                accessor: "iso3Code",
                width: 25,
            },
        ],
        [],
    );

    const fetchData = async (page: number, pageSize: number, sortBy: SortingRule<object>[] | null) => {
        //Atualizamos na history do browser a current page e pageSize
        var searchUrl: string = `?page=${page}&pageSize=${pageSize}${Utilities.AddParamToQueryIfValue(
            "name",
            "string",
            name
        )}${Utilities.AddParamToQueryIfValue(
            "iso2Code",
            "string",
            iso2Code
        )}${Utilities.AddParamToQueryIfValue(
            "iso3Code",
            "string",
            iso3Code
        )}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue("sortField", "string", sortBy[0].id)}${Utilities.AddParamToQueryIfValue(
                "sortOrder", "string", sortBy[0].desc === true ? "DESC" : "ASC",
            )}`;
        }
        
        //Se antes não existirem parâmetros de pesquisa, significa que o user veio para aqui de uma rota, então para não ficar com duas rotas duplicadas, fazemos replace à rota
        if (searchParams.toString() === "") {
            navigate(`/countries${searchUrl}`, { replace: true });
        } else {
            //Se já existirem parâmetros de pesquisa, não fazemos replace mas push
            navigate(`/countries${searchUrl}`);
        }

        setLoading(true);

        //Vamos obter os dados
        var response = await service.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize,
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(5);
            setCurrentPage(1);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }
        
        //Atribuímos os dados
        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0)

        //Se a pesquisa tiver retornado múltiplas páginas e a página atual for superior ao nº de páginas que chegaram mandamos o user para a página 1
        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };

    //Método para montar os parâmetros de pesquisa
    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];
        parameters.push(new Parameter("name", name));
        parameters.push(new Parameter("iso2Code", iso2Code));
        parameters.push(new Parameter("iso3Code", iso3Code));

        return parameters;
    };

    const onRemoveFiltersClick = () => {
        setCurrentPage(1);
        setGoToPage(1);
        setName("");
        setIso2Code("");
        setIso3Code("");
        setReloadSearch(true);
    }

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 10, currentSorting);
    };

    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 10, currentSorting);
    }

    useEffect(() => { 
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => {};
    }, [reloadSearch]);

    const onNewTaskClick = () => {
        navigate(Routes.countries.create);
        };

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <span className="pageTitle">
                        <FormattedMessage id="Screens.Country.Title" />
                    </span>
                </div>

                <Row>
                    <Col xl={2} lg={3} md={3} sm={12} xs={12} style={{ marginTop: "1em" }}>
                        <ButtonComponent
                            text={intl.formatMessage({ id: "Screens.Country.Button.CreateCountry" })}
                            onClick={onNewTaskClick} 
                        />
                    </Col>
                </Row>

                <div className='pageBody'>
                    <Col>
                        <Row>
                            <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                label={intl.formatMessage({ id: "Screens.Country.Filters.Name" })}
                                readOnly={false}
                                value={name}
                                onEnter={onEnter}
                                onChange={(t: string) => setName(t)}
                            />
                            <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                label={intl.formatMessage({ id: "Screens.Country.Filters.Iso2Code" })}
                                readOnly={false}
                                value={iso2Code}
                                onEnter={onEnter}
                                onChange={(t: string) => setIso2Code(t)}
                            />
                            <InputComponent xl={2} lg={3} md={5} sm={12} xs={12}
                                style={{ width: "100%" }}
                                type={"text"}
                                label={intl.formatMessage({ id: "Screens.Country.Filters.Iso3Code" })}
                                readOnly={false}
                                value={iso3Code}
                                onEnter={onEnter}
                                onChange={(t: string) => setIso3Code(t)}
                            />
                        </Row>
                    </Col>
                    <Row>
                        <Col xl={3} lg={3} md={3} sm={12} xs={12}
                            style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}>
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                onClick={onButtonClick}
                            />
                            <ButtonComponent
                                text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                onClick={onRemoveFiltersClick} 
                            />
                        </Col>
                    </Row>

                    <div className="pageComponent" style={{ width: "100%" }}>
                        <TableComponent
                            goToPage={goToPage}
                            loading={loading}
                            pageCount={pageCount}
                            columns={columns}
                            manualPagination={true}
                            fetchData={fetchData}
                            pageSizes={pageSizes}
                            initialPage={page}
                            initialPageSize={pageSize}
                            initialSortBy={initialSortBy}
                            data={data}
                            dontOrderFirstCollumn={true}
                        />
                    </div>
                </div>
            </div>
        </Layout>
    )
}
